import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { ShieldCheck, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

/**
 * ReviewerInviteAcceptPage
 *
 * Public landing page for compliance-review invitations.
 *
 * Behavior matrix:
 *   - Loading: spinner while we fetch the preview.
 *   - Preview not_found / network: friendly error card.
 *   - Preview is `revoked` / `expired` / `accepted`: show the personalized
 *     state copy with no auto-handshake (terminal state).
 *   - Preview is `pending` AND user is not logged in: show Sign in / Create
 *     account buttons; both carry `?invite=<token>` so the auth pages can
 *     bounce the reviewer back here after authentication.
 *   - Preview is `pending` AND user is logged in: auto-fire the accept
 *     handshake. On 200 → redirect to /reviews/{campaign_id}. On 403 (email
 *     mismatch) → show "wrong account" card with a "Sign out and use the
 *     invited address" button. On 410 → show expired/revoked card.
 *
 * The accept POST is idempotent server-side, so a refresh after acceptance
 * just re-runs the redirect path without creating a second binding.
 */

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'expired':
      return <Clock size={20} strokeWidth={1.75} color="#e6b35c" />;
    case 'revoked':
      return <AlertTriangle size={20} strokeWidth={1.75} color="#e67373" />;
    case 'accepted':
      return <CheckCircle2 size={20} strokeWidth={1.75} color="#7ae2a1" />;
    default:
      return <ShieldCheck size={20} strokeWidth={1.75} color="#7a9aff" />;
  }
};

const iconTint = (status) => {
  switch (status) {
    case 'expired':
      return { bg: 'rgba(230, 179, 92, 0.12)', border: 'rgba(230, 179, 92, 0.25)' };
    case 'revoked':
      return { bg: 'rgba(230, 115, 115, 0.12)', border: 'rgba(230, 115, 115, 0.25)' };
    case 'accepted':
      return { bg: 'rgba(122, 226, 161, 0.12)', border: 'rgba(122, 226, 161, 0.25)' };
    default:
      return { bg: 'rgba(65, 105, 225, 0.12)', border: 'rgba(65, 105, 225, 0.25)' };
  }
};

const headingCopy = (status) => {
  switch (status) {
    case 'expired':
      return 'This invitation has expired';
    case 'revoked':
      return 'This invitation was revoked';
    case 'accepted':
      return "You've already accepted this invitation";
    default:
      return "You've been invited to review";
  }
};

const ReviewerInviteAcceptPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const toast = useToast();

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [errorCode, setErrorCode] = useState(null);
  // 'idle' | 'accepting' | 'accepted' | 'mismatch' | 'gone' | 'failed'
  const [acceptState, setAcceptState] = useState('idle');
  const [acceptError, setAcceptError] = useState(null);
  const acceptInFlightRef = useRef(false);

  const continueHref = useCallback(
    (base) => {
      const qs = new URLSearchParams();
      if (token) qs.set('invite', token);
      return `${base}?${qs.toString()}`;
    },
    [token]
  );

  // ── Preview fetch ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setErrorCode('not_found');
      setPreviewLoading(false);
      return () => {};
    }
    (async () => {
      try {
        const { data } = await api.get(
          `/api/compliance/invites/${encodeURIComponent(token)}/preview`
        );
        if (cancelled) return;
        setPreview(data);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) setErrorCode('not_found');
        else setErrorCode('network');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // ── Accept handshake (auto-fires when user + pending preview ready) ──────
  useEffect(() => {
    if (authLoading || previewLoading) return;
    if (!user || !preview || !token) return;
    if (preview.status !== 'pending') return;
    if (acceptInFlightRef.current || acceptState !== 'idle') return;

    acceptInFlightRef.current = true;
    setAcceptState('accepting');

    (async () => {
      try {
        const { data } = await api.post('/api/compliance/invites/accept', {
          token,
        });
        setAcceptState('accepted');
        if (!data.was_already_accepted) {
          toast({
            title: 'Invitation accepted',
            description: `You can now review "${data.campaign_name}".`,
            status: 'success',
            duration: 3500,
            isClosable: true,
          });
        }
        // Land on the Reviews tab — the placeholder page lives at
        // /reviews/:campaignId, but Phase 2 sends them to the home tab so
        // they see the freshly-added card. Phase 3 swaps this for a direct
        // jump into the reviewer page.
        navigate(`/?tab=reviews`, { replace: true });
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 403) {
          setAcceptState('mismatch');
          setAcceptError(detail || null);
        } else if (status === 410) {
          setAcceptState('gone');
          setAcceptError(detail || null);
        } else if (status === 404) {
          setAcceptState('gone');
          setAcceptError(detail || 'This invitation link is no longer valid.');
        } else {
          setAcceptState('failed');
          setAcceptError(
            detail || 'Something went wrong. Please try again in a moment.'
          );
        }
      } finally {
        acceptInFlightRef.current = false;
      }
    })();
  }, [authLoading, previewLoading, user, preview, token, acceptState, navigate, toast]);

  // ── Sign-out + redirect for the email-mismatch case ───────────────────────
  const handleSignOutAndRetry = async () => {
    try {
      await logout();
    } catch {
      // logout() already swallows API errors, this catch is just for the
      // navigate call below if logout's internal navigate races.
    }
    navigate(continueHref('/login'), { replace: true });
  };

  // ── Card body selector ────────────────────────────────────────────────────
  const renderCardBody = () => {
    if (previewLoading || authLoading) {
      return (
        <Flex align="center" justify="center" minH="180px">
          <Spinner size="sm" color="rgba(255,255,255,0.55)" thickness="2px" />
        </Flex>
      );
    }

    if (errorCode === 'not_found') {
      return (
        <ErrorCard
          tint="red"
          icon={<AlertTriangle size={20} strokeWidth={1.75} color="#e67373" />}
          heading="This invitation link isn't valid"
          body="We couldn't find a compliance-review invitation for this link. It may have been revoked, replaced by a newer invite, or mistyped. Ask the person who sent it to forward the most recent email."
        />
      );
    }

    if (errorCode === 'network') {
      return (
        <ErrorCard
          tint="amber"
          icon={<AlertTriangle size={20} strokeWidth={1.75} color="#e6b35c" />}
          heading="We couldn't load this invitation"
          body="Something went wrong reaching GillyReach. Refresh the page in a moment, or contact the person who sent you this link."
        />
      );
    }

    const advisorName = preview?.advisor_display_name || 'A GillyReach user';
    const advisorEmail = preview?.advisor_email || '';
    const campaignName = preview?.campaign_name || 'a GillyReach campaign';
    const maskedEmail = preview?.invited_email_masked || null;
    const previewStatus = preview?.status || 'pending';

    // Email mismatch trumps preview status — wrong account, regardless.
    if (acceptState === 'mismatch') {
      return (
        <VStack spacing={5} align="stretch">
          <HStack spacing={3} align="center">
            <Box
              p={2.5}
              borderRadius="8px"
              bg="rgba(230, 115, 115, 0.12)"
              border="1px solid rgba(230, 115, 115, 0.25)"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <AlertTriangle size={20} strokeWidth={1.75} color="#e67373" />
            </Box>
            <Heading size="md" color="#fafafa" fontWeight="600" letterSpacing="-0.01em">
              Wrong account
            </Heading>
          </HStack>
          <Text fontSize="sm" color="rgba(255,255,255,0.7)" lineHeight="1.65">
            {acceptError ||
              'This invitation was sent to a different email address. Sign in with the address the invitation was sent to.'}
            {maskedEmail && (
              <>
                {' '}The invitation was sent to{' '}
                <Text as="span" color="rgba(255,255,255,0.95)" fontWeight="600">
                  {maskedEmail}
                </Text>
                .
              </>
            )}
          </Text>
          <VStack spacing={2} align="stretch">
            <Button
              bg="#fafafa"
              color="#0a0a0a"
              borderRadius="full"
              fontWeight="600"
              fontSize="sm"
              h="40px"
              _hover={{ opacity: 0.88 }}
              _active={{ opacity: 0.75 }}
              onClick={handleSignOutAndRetry}
            >
              Sign out and use the invited address
            </Button>
          </VStack>
        </VStack>
      );
    }

    // Server says the link itself is dead — overrides the preview.
    if (acceptState === 'gone') {
      return (
        <ErrorCard
          tint="amber"
          icon={<Clock size={20} strokeWidth={1.75} color="#e6b35c" />}
          heading="This invitation is no longer active"
          body={
            acceptError ||
            'This invitation link has expired or been revoked. Ask the sender for a fresh invite.'
          }
        />
      );
    }

    if (acceptState === 'failed') {
      return (
        <ErrorCard
          tint="amber"
          icon={<AlertTriangle size={20} strokeWidth={1.75} color="#e6b35c" />}
          heading="We couldn't accept this invitation"
          body={acceptError || 'Please try again in a moment.'}
        />
      );
    }

    // Happy + state-aware path (pending / accepted / expired / revoked).
    const tint = iconTint(previewStatus);
    const canProceed = previewStatus === 'pending' || previewStatus === 'accepted';
    const isAccepting = acceptState === 'accepting';

    return (
      <VStack spacing={6} align="stretch">
        <HStack spacing={3} align="center">
          <Box
            p={2.5}
            borderRadius="8px"
            bg={tint.bg}
            border={`1px solid ${tint.border}`}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <StatusIcon status={previewStatus} />
          </Box>
          <Heading size="md" color="#fafafa" fontWeight="600" letterSpacing="-0.01em">
            {headingCopy(previewStatus)}
          </Heading>
        </HStack>

        <Text fontSize="sm" color="rgba(255,255,255,0.78)" lineHeight="1.65">
          <Text as="span" fontWeight="600" color="#fafafa">
            {advisorName}
          </Text>
          {advisorEmail && (
            <Text as="span" color="rgba(255,255,255,0.55)">
              {' '}({advisorEmail})
            </Text>
          )}
          {' '}has invited you to compliance-review the outreach drafts in{' '}
          <Text as="span" fontWeight="600" color="#fafafa">
            {campaignName}
          </Text>
          . To keep reviewer identity auditable, you'll continue inside your
          own GillyReach account.
        </Text>

        {previewStatus === 'expired' && (
          <Text fontSize="sm" color="rgba(230, 179, 92, 0.9)" lineHeight="1.6">
            This link has expired. Ask {advisorName} to send a fresh invite,
            then come back to this page with the new link.
          </Text>
        )}

        {previewStatus === 'revoked' && (
          <Text fontSize="sm" color="rgba(230, 115, 115, 0.9)" lineHeight="1.6">
            {advisorName} revoked this invitation. If you think that was a
            mistake, reply to their email and ask for a new link.
          </Text>
        )}

        {canProceed && (
          <>
            {!user && (
              <Text fontSize="sm" color="rgba(255,255,255,0.5)" lineHeight="1.65">
                Sign in with
                {maskedEmail ? (
                  <>
                    {' '}the address this invitation was sent to (
                    <Text as="span" color="rgba(255,255,255,0.75)">{maskedEmail}</Text>
                    )
                  </>
                ) : (
                  ' the address this invitation was sent to'
                )}
                , or create a free account using that same address. The shared
                campaign will then appear under your{' '}
                <Text as="span" fontWeight="600" color="#fafafa">Reviews</Text>{' '}
                tab on the home page.
              </Text>
            )}

            {isAccepting && (
              <HStack spacing={2} color="rgba(255,255,255,0.55)">
                <Spinner size="xs" thickness="2px" />
                <Text fontSize="sm">Accepting your invitation…</Text>
              </HStack>
            )}

            {!user && !isAccepting && (
              <VStack spacing={2} align="stretch">
                <Button
                  bg="#fafafa"
                  color="#0a0a0a"
                  borderRadius="full"
                  fontWeight="600"
                  fontSize="sm"
                  h="40px"
                  _hover={{ opacity: 0.88 }}
                  _active={{ opacity: 0.75 }}
                  onClick={() => navigate(continueHref('/login'))}
                >
                  Sign in
                </Button>
                <Button
                  as={RouterLink}
                  to={continueHref('/register')}
                  variant="ghost"
                  color="rgba(255,255,255,0.7)"
                  borderRadius="full"
                  fontWeight="500"
                  fontSize="sm"
                  h="40px"
                  border="1px solid rgba(255,255,255,0.12)"
                  _hover={{
                    color: '#fafafa',
                    borderColor: 'rgba(255,255,255,0.25)',
                    bg: 'rgba(255,255,255,0.04)',
                  }}
                >
                  Create a free account
                </Button>
              </VStack>
            )}
          </>
        )}
      </VStack>
    );
  };

  return (
    <Flex align="center" justify="center" minH="100vh" bg="#0a0a0a" px={4}>
      <Box
        maxW="md"
        w="100%"
        p={8}
        borderRadius="12px"
        border="1px solid rgba(255,255,255,0.10)"
        bg="#0f0f0f"
      >
        {renderCardBody()}
      </Box>
    </Flex>
  );
};

const ErrorCard = ({ tint, icon, heading, body }) => {
  const tintMap = {
    red: { bg: 'rgba(230, 115, 115, 0.12)', border: 'rgba(230, 115, 115, 0.25)' },
    amber: { bg: 'rgba(230, 179, 92, 0.12)', border: 'rgba(230, 179, 92, 0.25)' },
  };
  const t = tintMap[tint] || tintMap.amber;
  return (
    <VStack spacing={4} align="stretch">
      <HStack spacing={3} align="center">
        <Box
          p={2.5}
          borderRadius="8px"
          bg={t.bg}
          border={`1px solid ${t.border}`}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {icon}
        </Box>
        <Heading size="md" color="#fafafa" fontWeight="600" letterSpacing="-0.01em">
          {heading}
        </Heading>
      </HStack>
      <Text fontSize="sm" color="rgba(255,255,255,0.65)" lineHeight="1.65">
        {body}
      </Text>
    </VStack>
  );
};

export default ReviewerInviteAcceptPage;
